import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface TicketBandConfig {
  key: string
  label: string
  minCents: number
  maxCents: number | null
}

interface TicketBandRow {
  key: string
  label: string
  orderCount: number
  totalAmountCents: number
  averageTicketCents: number
  orderSharePercent: number
  ninegridOrders: number
  totvsOrders: number
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const ticketBands: TicketBandConfig[] = [
  { key: 'up-to-100', label: 'Ate R$ 100', minCents: 0, maxCents: 10000 },
  { key: '100-200', label: 'R$ 100 a R$ 200', minCents: 10001, maxCents: 20000 },
  { key: '200-500', label: 'R$ 200 a R$ 500', minCents: 20001, maxCents: 50000 },
  { key: '500-1000', label: 'R$ 500 a R$ 1.000', minCents: 50001, maxCents: 100000 },
  { key: 'above-1000', label: 'Acima de R$ 1.000', minCents: 100001, maxCents: null },
]

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const exportToCSV = (filename: string, rows: TicketBandRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Faixa;Pedidos;Participacao (%);Faturamento;Ticket médio;Ninegrid;TOTVS\n'

  rows.forEach((row) => {
    csv += `"${row.label}";${row.orderCount};${row.orderSharePercent.toFixed(1)};${row.totalAmountCents / 100};${row.averageTicketCents / 100};${row.ninegridOrders};${row.totvsOrders}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: TicketBandRow[]): void => {
  const data = rows.map((row) => ({
    Faixa: row.label,
    Pedidos: row.orderCount,
    'Participacao (%)': row.orderSharePercent.toFixed(1),
    Faturamento: row.totalAmountCents / 100,
    'Ticket medio': row.averageTicketCents / 100,
    Ninegrid: row.ninegridOrders,
    TOTVS: row.totvsOrders,
  }))

  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Faixas')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, rows: TicketBandRow[], report: SellerTicketAverageReport | null): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Faixas de Ticket', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Pedidos totais: ${report?.summary.totalOrders || 0} | Ticket médio geral: ${formatCurrency(report?.summary.averageTicketCents || 0)}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Faixa', 'Pedidos', 'Participacao (%)', 'Faturamento', 'Ticket medio', 'Ninegrid', 'TOTVS']],
    body: rows.map((row) => [
      row.label,
      row.orderCount,
      row.orderSharePercent.toFixed(1),
      formatCurrency(row.totalAmountCents),
      formatCurrency(row.averageTicketCents),
      row.ninegridOrders,
      row.totvsOrders,
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const getBandForTicket = (ticketCents: number): TicketBandConfig => {
  return (
    ticketBands.find(
      (band) => ticketCents >= band.minCents && (band.maxCents === null || ticketCents <= band.maxCents)
    ) || ticketBands[ticketBands.length - 1]
  )
}

const buildTicketBandRows = (report: SellerTicketAverageReport | null): TicketBandRow[] => {
  const initialMap = new Map(
    ticketBands.map((band) => [
      band.key,
      {
        key: band.key,
        label: band.label,
        orderCount: 0,
        totalAmountCents: 0,
        averageTicketCents: 0,
        orderSharePercent: 0,
        ninegridOrders: 0,
        totvsOrders: 0,
      },
    ])
  )

  if (!report?.items.length) {
    return Array.from(initialMap.values())
  }

  for (const order of report.items) {
    const orderTotal = typeof order.total === 'number' ? Math.round(order.total) : 0
    const band = getBandForTicket(orderTotal)
    const current = initialMap.get(band.key)

    if (!current) {
      continue
    }

    current.orderCount += 1
    current.totalAmountCents += orderTotal

    if (order.source === 'ninegrid') {
      current.ninegridOrders += 1
    }

    if (order.source === 'totvs') {
      current.totvsOrders += 1
    }
  }

  const totalOrders = report.summary.totalOrders || 0

  return ticketBands.map((band) => {
    const row = initialMap.get(band.key)!

    return {
      ...row,
      averageTicketCents: row.orderCount > 0 ? Math.round(row.totalAmountCents / row.orderCount) : 0,
      orderSharePercent: totalOrders > 0 ? (row.orderCount / totalOrders) * 100 : 0,
    }
  })
}

const TicketBandsReportPage: FC = () => {
  const { filters, setFilters, isReady } = useSellerReportFilters<SellerTicketAverageFilters>({
    ...createDefaultSellerReportDateRange(),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatorio',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const rows = useMemo(() => buildTicketBandRows(report), [report])
  const topVolumeBand = useMemo(
    () => [...rows].sort((left, right) => right.orderCount - left.orderCount)[0] || null,
    [rows]
  )
  const topRevenueBand = useMemo(
    () => [...rows].sort((left, right) => right.totalAmountCents - left.totalAmountCents)[0] || null,
    [rows]
  )

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Faixas de Ticket',
            path: '/seller/reports/ticket-bands',
            isActive: true,
          },
        ]}
      >
        Relatório de Faixas de Ticket
      </PageTitle>

      <ReportShell
        title='Distribuição por Faixa de Ticket'
        description='Agrupa os pedidos por faixa de valor para identificar perfil de compra, concentração de pedidos e comportamento por origem.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Faixas-Ticket-${timestamp}.csv`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Faixas-Ticket-${timestamp}.xlsx`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Faixas-Ticket-${timestamp}.pdf`, rows, report)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Faixa com maior volume</div>
                <div className='fs-3 fw-bold text-primary'>
                  {topVolumeBand ? topVolumeBand.label : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-primary'>
                  {topVolumeBand ? `${topVolumeBand.orderCount} pedidos` : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Faixa com maior faturamento</div>
                <div className='fs-3 fw-bold text-success'>
                  {topRevenueBand ? topRevenueBand.label : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-success'>
                  {topRevenueBand ? formatCurrency(topRevenueBand.totalAmountCents) : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Ticket medio geral</div>
                <div className='fs-2hx fw-bold text-dark'>
                  {formatCurrency(report?.summary.averageTicketCents || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Faixas de ticket</span>
              <span className='text-gray-600 mt-1'>Volume, faturamento e participacao por faixa de valor.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : rows.some((row) => row.orderCount > 0) ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Faixa</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Participacao</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ticket medio</th>
                      <th className='text-end'>Ninegrid</th>
                      <th className='text-end'>TOTVS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td>
                          <div className='fw-bold text-dark'>{row.label}</div>
                        </td>
                        <td className='text-end fw-bold'>{row.orderCount}</td>
                        <td className='text-end'>{percentFormatter.format(row.orderSharePercent)}%</td>
                        <td className='text-end fw-bold text-success'>
                          {formatCurrency(row.totalAmountCents)}
                        </td>
                        <td className='text-end'>{formatCurrency(row.averageTicketCents)}</td>
                        <td className='text-end text-primary'>{row.ninegridOrders}</td>
                        <td className='text-end text-warning'>{row.totvsOrders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { TicketBandsReportPage }
